import { Inject, Injectable, inject, signal, InjectionToken, Optional } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import {
	IRaceRgps,
	IRaceSession,
	IRaceControl,
	ITimingAnalysis,
} from '../timing-tower/IRaceTiming';
import { FeatureSettingsService } from './feature-settings.service';
import { SERVER_ENDPOINT } from './websocket.token';
import { VbLxpAuthService } from '../authentication/vb-lxp-auth.service';
import { LDataTypeService } from './ldatatype.service';

export interface WebSocketConfig {
	requiresAuth?: boolean;
}

export const WEBSOCKET_CONFIG = new InjectionToken<WebSocketConfig>('websocket.config', {
	providedIn: 'root',
	factory: () => ({ requiresAuth: false }), // Default: no auth required (backward compatible)
});

@Injectable({
	providedIn: 'root',
})
export class WebSocketService {
	private eventSource: EventSource | null = null;
	private reconnectAttempts = 0;
	private readonly maxReconnectAttempts = 5;
	private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;

	private readonly sessionSubject = new BehaviorSubject<{
		session: IRaceSession;
		isSessionArchived: boolean;
	}>({} as { session: IRaceSession; isSessionArchived: boolean });
	private readonly rgpsSubject = new BehaviorSubject<IRaceRgps>({} as IRaceRgps);
	private readonly mainStreamSubject = new BehaviorSubject<any>(null);
	private readonly supportStreamSubject = new BehaviorSubject<any>(null);
	private readonly raceControlSubject = new BehaviorSubject<IRaceControl>({} as IRaceControl);

	private readonly analysisSubject = new BehaviorSubject<ITimingAnalysis>({} as ITimingAnalysis);

	// TODO: Finish making interfaces for these as needed. As it stands these channels are not used ATM
	private readonly combinedSubject = new BehaviorSubject<any>({} as any);
	private readonly weatherSubject = new BehaviorSubject<any>({} as any);
	private readonly gpsSubject = new BehaviorSubject<any>({} as any);
	private readonly telemEventSubject = new BehaviorSubject<any>({} as any);

	private readonly featureSettingsService = inject(FeatureSettingsService);
	private readonly authService = inject(VbLxpAuthService);
	private readonly lDataTypeService = inject(LDataTypeService);

	public isConnected = signal(false); // Signal to track connection status

	public session$ = this.sessionSubject.asObservable();
	public rgps$ = this.rgpsSubject.asObservable();
	public weather$ = this.weatherSubject.asObservable();
	public mainStream$ = this.mainStreamSubject.asObservable();
	public supportStream$ = this.supportStreamSubject.asObservable();
	public raceControl$ = this.raceControlSubject.asObservable();
	public timingAnalysis$ = this.analysisSubject.asObservable();

	constructor(
		@Inject(SERVER_ENDPOINT) private serverEndpoint: string,
		@Optional() @Inject(WEBSOCKET_CONFIG) private config?: WebSocketConfig,
	) {
		// Don't auto-connect - let components call connect() when needed
	}

	/**
	 * Manually connect to SSE. Should be called only when user is authenticated (if auth is required).
	 */
	public connect(): void {
		// Only check authentication if the config requires it
		if (this.config?.requiresAuth && !this.isAuthenticated()) {
			console.warn('Cannot connect to SSE: User is not authenticated');
			return;
		}

		if (this.eventSource) {
			console.log('SSE already connected');
			return;
		}

		this.initializeSSE();
	}

	/**
	 * Check if user is authenticated
	 */
	private isAuthenticated(): boolean {
		return this.authService.isAuthenticated();
	}

	private initializeSSE() {
		// Double-check authentication before proceeding (only if required)
		if (this.config?.requiresAuth && !this.isAuthenticated()) {
			console.warn('SSE initialization aborted: User not authenticated');
			return;
		}

		const envPrefix = this.lDataTypeService.getDataType();

		// Build SSE URL with token as query parameter (EventSource doesn't support Authorization header)
		console.log('serverEndpoint: ', this.serverEndpoint);
		let sseUrl = `${this.serverEndpoint}events`;
		const token = this.authService.getToken();
		if (token) {
			sseUrl += `?token=${encodeURIComponent(token)}`;
		}

		this.eventSource = new EventSource(sseUrl, {
			withCredentials: true,
		});

		this.eventSource.onopen = () => {
			console.log('SSE connection opened');
			this.isConnected.set(true);
			this.reconnectAttempts = 0; // Reset reconnection counter on successful connection
		};

		this.eventSource.addEventListener('app-features-update', (event) => {
			try {
				const data = JSON.parse(event.data);
				this.featureSettingsService.updateFeatureSettings(data);
				console.warn('Received app-features-update:', data);
			} catch (error) {
				console.error('Error parsing data-update event:', error);
			}
		});

		this.eventSource.addEventListener('main-stream-update', (event) => {
			try {
				const data = JSON.parse(event.data);
				this.mainStreamSubject.next(data);
			} catch (error) {
				console.error('Error parsing main-stream-update event:', error);
			}
		});

		this.eventSource.addEventListener('support-stream-update', (event: any) => {
			try {
				const data = JSON.parse(event.data);
				this.supportStreamSubject.next(data);
			} catch (error) {
				console.error('Error parsing support-stream-update event:', error);
			}
		});

		// New multi-channel event listeners
		const channelEvents = [
			'timing.session',
			'timing.combined',
			'timing.analysis',
			'raceControl',
			'weather',
			'gps',
			'rgps',
			'telemEvent',
		];

		// Subscribe to all channel events
		channelEvents.forEach((channel) => {
			const eventName = `${envPrefix}-${channel}`;
			this.eventSource?.addEventListener(eventName, (event) => {
				try {
					const data = JSON.parse(event.data);
					if (data) {
						switch (channel) {
							case 'timing.session':
								this.sessionSubject.next({
									session: data.session,
									isSessionArchived: data.isSessionArchived,
								});
								break;
							case 'timing.combined':
								this.combinedSubject.next(data.combined);
								break;

							case 'timing.analysis':
								this.analysisSubject.next(data.analysis);
								break;

							case 'raceControl':
								this.raceControlSubject.next(data.raceControl);
								break;
							case 'weather':
								this.weatherSubject.next(data.weather);
								break;
							case 'gps':
								this.gpsSubject.next(data.gps);
								break;
							case 'rgps':
								this.rgpsSubject.next(data.rgps);
								break;
							case 'telemEvent':
								this.telemEventSubject.next(data.telemEvent);
								break;
						}
					}
				} catch (error) {
					console.error(`Error parsing ${eventName} event:`, error);
				}
			});
		});

		this.eventSource.onerror = (error) => {
			console.error('SSE connection error:', error);
			this.isConnected.set(false);

			// Close the current connection
			if (this.eventSource) {
				this.eventSource.close();
				this.eventSource = null;
			}

			// Only attempt to reconnect if authenticated and haven't exceeded max attempts
			// (or if auth is not required)
			const canReconnect =
				(!this.config?.requiresAuth || this.isAuthenticated()) &&
				this.reconnectAttempts < this.maxReconnectAttempts;

			if (canReconnect) {
				this.reconnectAttempts++;
				const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts - 1), 30000); // Exponential backoff, max 30s
				console.log(
					`SSE connection closed, attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts}) in ${delay / 1000}s...`,
				);
				this.reconnectTimeout = setTimeout(() => {
					this.initializeSSE();
				}, delay);
			} else if (this.config?.requiresAuth && !this.isAuthenticated()) {
				console.log('SSE connection closed: User not authenticated, will not reconnect');
				this.reconnectAttempts = 0;
			} else {
				console.error('SSE max reconnection attempts reached, giving up');
			}
		};
	}

	public disconnect() {
		// Clear any pending reconnection attempts
		if (this.reconnectTimeout) {
			clearTimeout(this.reconnectTimeout);
			this.reconnectTimeout = null;
		}

		// Close the SSE connection
		if (this.eventSource) {
			this.eventSource.close();
			this.eventSource = null;
			this.isConnected.set(false);
		}

		// Reset reconnection counter
		this.reconnectAttempts = 0;

		// Clear all data subjects
		this.clearAllData();

		console.log('SSE connection disconnected and data cleared');
	}

	/**
	 * Clear all data from BehaviorSubjects
	 */
	private clearAllData() {
		this.sessionSubject.next({} as { session: IRaceSession; isSessionArchived: boolean });
		this.rgpsSubject.next({} as IRaceRgps);
		this.mainStreamSubject.next(null);
		this.supportStreamSubject.next(null);
		this.combinedSubject.next({} as any);

		this.analysisSubject.next({} as any);
		this.raceControlSubject.next({} as any);
		this.weatherSubject.next({} as any);
		this.gpsSubject.next({} as any);
		this.telemEventSubject.next({} as any);
	}
}
